"use client"
import { createContext, useContext, useState, useCallback } from "react"
import { Alert, Snackbar, Box, Typography, alpha } from "@mui/material"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"

// Create context with default values
const ToastContext = createContext({
  showToast: () => {},
})

// Toast severity types
export const TOAST_SEVERITY = {
  SUCCESS: "success",
  ERROR: "error",
  WARNING: "warning",
  INFO: "info",
}

// Toast provider component
export const ToastProvider = ({ children }) => {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState("")
  const [severity, setSeverity] = useState(TOAST_SEVERITY.INFO)
  const [duration, setDuration] = useState(6000)

  const showToast = useCallback((message, severity = TOAST_SEVERITY.INFO, duration = 5000) => {
    setMessage(message)
    setSeverity(severity)
    setDuration(duration)
    setOpen(true)
    console.log("Toast triggered:", { message, severity, duration }) // Debug log
  }, [])

  const handleClose = useCallback((event, reason) => {
    if (reason === "clickaway") {
      return
    }
    setOpen(false)
  }, [])

  // Get icon based on severity
  const getIcon = (severity) => {
    switch (severity) {
      case TOAST_SEVERITY.SUCCESS:
        return <CheckCircleOutlineIcon sx={{ fontSize: "1.5rem" }} />
      case TOAST_SEVERITY.ERROR:
        return <ErrorOutlineIcon sx={{ fontSize: "1.5rem" }} />
      case TOAST_SEVERITY.WARNING:
        return <WarningAmberIcon sx={{ fontSize: "1.5rem" }} />
      case TOAST_SEVERITY.INFO:
      default:
        return <InfoOutlinedIcon sx={{ fontSize: "1.5rem" }} />
    }
  }

  // Get background color based on severity
  const getBackgroundColor = (severity) => {
    switch (severity) {
      case TOAST_SEVERITY.SUCCESS:
        return "rgba(0, 229, 255, 0.15)"
      case TOAST_SEVERITY.ERROR:
        return "rgba(244, 67, 54, 0.15)"
      case TOAST_SEVERITY.WARNING:
        return "rgba(255, 152, 0, 0.15)"
      case TOAST_SEVERITY.INFO:
      default:
        return "rgba(0, 229, 255, 0.15)"
    }
  }

  // Get border color based on severity
  const getBorderColor = (severity) => {
    switch (severity) {
      case TOAST_SEVERITY.SUCCESS:
        return "rgba(0, 229, 255, 0.5)"
      case TOAST_SEVERITY.ERROR:
        return "rgba(244, 67, 54, 0.5)"
      case TOAST_SEVERITY.WARNING:
        return "rgba(255, 152, 0, 0.5)"
      case TOAST_SEVERITY.INFO:
      default:
        return "rgba(0, 229, 255, 0.5)"
    }
  }

  // Get icon color based on severity
  const getIconColor = (severity) => {
    switch (severity) {
      case TOAST_SEVERITY.SUCCESS:
        return "#00e5ff"
      case TOAST_SEVERITY.ERROR:
        return "#f44336"
      case TOAST_SEVERITY.WARNING:
        return "#ff9800"
      case TOAST_SEVERITY.INFO:
      default:
        return "#00e5ff"
    }
  }

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{ zIndex: 9999 }}
      >
        <Box
          sx={{
            minWidth: 300,
            maxWidth: 400,
            backgroundColor: "rgba(13, 15, 25, 0.95)",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
            border: `1px solid ${getBorderColor(severity)}`,
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Decorative top border */}
          <Box
            sx={{
              height: "3px",
              background: `linear-gradient(90deg, ${getIconColor(severity)} 0%, ${alpha(
                getIconColor(severity),
                0.3,
              )} 50%, rgba(0, 0, 0, 0) 100%)`,
              width: "100%",
            }}
          />

          <Box
            sx={{
              display: "flex",
              p: 2,
              alignItems: "center",
              gap: 2,
              backgroundColor: getBackgroundColor(severity),
            }}
          >
            <Box
              sx={{
                color: getIconColor(severity),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textShadow: `0 0 5px ${getIconColor(severity)}`,
              }}
            >
              {getIcon(severity)}
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: "white",
                fontWeight: 500,
                textShadow: severity === TOAST_SEVERITY.SUCCESS ? "0 0 5px rgba(0, 229, 255, 0.3)" : "none",
              }}
            >
              {message}
            </Typography>
          </Box>
        </Box>
      </Snackbar>
    </ToastContext.Provider>
  )
}

// Custom hook to use the toast context
export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider")
  }
  return context
}
